import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";

const TITLE = "How Much Does It Cost to Contest a Will in QLD?";
const DESCRIPTION =
  "How much does it cost to contest a will in Queensland? What a family provision claim really costs — stage by stage, who pays, no-win-no-fee traps, and how to keep costs proportionate to the estate.";

const FAQS = [
  {
    q: "How much does it cost to contest a will in Queensland?",
    a: "It depends almost entirely on how far the claim goes. A claim resolved by notice and negotiation is the cheapest outcome by a wide margin. A claim that settles at court-ordered mediation — where most Queensland family provision claims end — costs meaningfully more, because affidavit material, financial disclosure and the mediator's fee are all involved. A claim that runs to trial with counsel is in a different category again, and the cost falls on the estate as well as the parties. We will give you a costs estimate for each stage in writing before you commit.",
  },
  {
    q: "Who pays the legal costs of contesting a will?",
    a: "Not automatically the estate — this is the most common and most expensive misconception. Costs are in the court's discretion under the Succession Act framework and the Uniform Civil Procedure Rules. A successful applicant often has costs paid out of the estate, but Queensland courts have become markedly less willing to indemnify applicants who run weak claims, particularly against modest estates. An unsuccessful applicant can be ordered to pay their own costs and the estate's.",
  },
  {
    q: "Does the executor's defence come out of the estate?",
    a: "An executor's reasonable costs of defending a claim are generally payable from the estate, because the executor is acting in that role rather than personally. That protection is not unlimited. An executor who unreasonably refuses a sensible offer, or defends an obviously meritorious claim to trial, can be left personally exposed to costs. Executors should take advice on offers, not just on the defence.",
  },
  {
    q: "Is no win, no fee available for estate claims?",
    a: "Some firms offer conditional costs agreements for family provision claims. Before signing, check four things: the uplift percentage payable on success, whether disbursements (counsel, mediator, filing fees, medical reports) are funded or payable by you regardless, what happens if the claim settles for less than an offer already on the table, and whether you remain personally liable for the estate's costs if you lose. No win, no fee limits your exposure to your own solicitor's fees — it does not remove the risk of an adverse costs order.",
  },
  {
    q: "Is it worth contesting a small estate?",
    a: "Often not, and we will tell you that at the first appointment. Where the whole estate is modest, costs on both sides can consume a large share of what is in dispute, and the court is alive to that. Where a claim is genuinely meritorious the answer can still be yes — but the strategy has to be early notice, complete disclosure and a realistic offer, not a slow march toward a hearing.",
  },
  {
    q: "How long do I have to contest a will in Queensland?",
    a: "You must give written notice of your intention to make a family provision claim within six months of the date of death, and the application must be filed within nine months of death. Notice within six months also prevents the executor from distributing the estate without regard to your claim. Late applications need the court's leave and are much harder and more expensive to run.",
  },
  {
    q: "How long does a family provision claim take?",
    a: "Claims that resolve in negotiation or at mediation commonly finish within six to twelve months of the date of death. Claims that go to trial routinely run beyond two years, which is the main reason trial costs are so much higher — it is time as much as complexity.",
  },
  {
    q: "What does a first appointment cost?",
    a: `Call ${FIRM.phone} and we will tell you what the initial appointment costs before you book it, and give you a candid view of whether the claim is worth running. We would rather turn a matter away early than run one that cannot pay for itself.`,
  },
];

export const Route = createFileRoute("/cost-to-contest-a-will")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/cost-to-contest-a-will" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/cost-to-contest-a-will" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — Family Provision Claims`,
          description: DESCRIPTION,
          telephone: FIRM.phone,
          areaServed: [
            { "@type": "State", name: "Queensland" },
            { "@type": "Place", name: "Sunshine Coast" },
            { "@type": "Place", name: "Moreton Bay" },
            { "@type": "Place", name: "Brisbane" },
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: FIRM.street,
            addressLocality: FIRM.suburb,
            addressRegion: FIRM.state,
            postalCode: FIRM.postcode,
            addressCountry: FIRM.country,
          },
          serviceType: [
            "Contesting a will",
            "Family provision claim",
            "Estate dispute",
            "Defending an estate claim",
          ],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: CostToContestPage,
});

const STAGES = [
  {
    stage: "Stage 1 — Notice and negotiation",
    when: "Months 0–6 from date of death",
    cost: "Lowest cost by a wide margin",
    body: "A written notice of intended claim to the executor, financial disclosure from you, a schedule of the estate from them, and an offer. Many claims end here. If yours is going to settle, this is the stage where the most money is left in the estate for you to receive.",
  },
  {
    stage: "Stage 2 — Filing and mediation",
    when: "Months 6–12",
    cost: "Moderate",
    body: "Application filed within nine months of death, affidavits of your financial position and relationship with the deceased, the executor's affidavit of estate assets, and a court-ordered mediation. The mediator's fee is usually shared. Most Queensland family provision claims resolve at this point.",
  },
  {
    stage: "Stage 3 — Trial",
    when: "Year 2 and beyond",
    cost: "Highest — and it falls on the estate too",
    body: "Counsel briefed, subpoenas, cross-examination on affidavits, a listed hearing. Costs escalate for both sides simultaneously, and the pool being fought over shrinks accordingly. A small number of claims genuinely need to be tried; most do not.",
  },
];

const DRIVERS = [
  {
    t: "Estate size and complexity",
    b: "A single unencumbered house is straightforward. Companies, trusts, superannuation with binding nominations, jointly held property and farming assets each add valuation and legal work.",
  },
  {
    t: "Quality of your disclosure",
    b: "Incomplete or late financial disclosure is the single largest cause of wasted cost. Every gap generates correspondence, and correspondence is billable on both sides.",
  },
  {
    t: "How many parties are claiming",
    b: "Two adult children claiming against the same estate is a different exercise from one — competing needs must be evidenced and the mediation is harder to settle.",
  },
  {
    t: "Whether capacity or undue influence is alleged",
    b: "Challenging the validity of a will is a separate and more expensive fight than a family provision claim: medical records, the solicitor's file, and often expert evidence.",
  },
  {
    t: "How reasonable the other side is",
    b: "An executor who engages and discloses keeps costs down. One who ignores notice or defends reflexively drives them up — and can be personally exposed for doing so.",
  },
  {
    t: "How early a realistic offer is made",
    b: "Offers matter for costs. A well-pitched offer that the other side beats nothing on shifts the costs risk onto them from the date it was made.",
  },
];

function CostToContestPage() {
  return (
    <>
      <PageHero
        eyebrow="Estate disputes"
        title="How much does it cost to contest a will in Queensland?"
        cta="Ask about contesting a will"
        lead="The honest answer is that it depends on when the claim resolves — and almost all of the cost sits after mediation fails. Here is where the money actually goes, and how to keep it proportionate."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
          <p>
            No solicitor can quote a single figure for contesting a will, and you should be wary of
            one who does. What we can do — and do at the first appointment — is tell you what each
            stage costs, what your claim is realistically worth, and whether the two figures make
            sense together. If they do not, we will tell you that too.
          </p>
          <p>
            What follows applies to a family provision claim under the Succession Act 1981 (Qld) —
            the usual way an eligible spouse, child or dependant seeks further provision from an
            estate. Challenging the validity of a will (capacity, undue influence, forgery) is a
            different and generally more expensive exercise.
          </p>
        </div>

        <div className="mt-10 border-l-2 border-primary pl-5">
          <h2 className="font-serif text-2xl text-foreground">Before spending money on a claim</h2>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-muted-foreground">
            <li>Check the date of death and the applicable time limits promptly.</li>
            <li>Bring the will, details of the estate and a summary of your financial circumstances.</li>
            <li>Ask for a written estimate separating legal fees, outlays and potential costs payable to the other side.</li>
            <li>Compare the likely benefit with the cost of negotiation, mediation and a hearing.</li>
          </ul>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            <a href="#enquiry" className="font-semibold text-primary underline">Ask for an initial assessment</a>{" "}
            before committing to legal proceedings. We will explain the appointment fee before you book.
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">The three stages, and the cost step</h2>
          <div className="mt-8 space-y-6">
            {STAGES.map((s) => (
              <div key={s.stage} className="rounded-sm border border-border bg-card p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground">
                  {s.when}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-foreground">{s.stage}</h3>
                <p className="mt-1 text-sm font-semibold text-foreground">{s.cost}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
            The practical point: the difference in cost between settling at mediation and running to
            trial is far larger than the difference between doing nothing and giving notice. Getting
            the early stages right is what protects the value of the claim.
          </p>
        </div>

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Who actually pays?</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The belief that "the estate pays for everything" is the most expensive assumption in
            estate litigation. Costs are discretionary. A successful applicant is often paid out of
            the estate; an unsuccessful one can be ordered to pay their own costs and the estate's,
            and Queensland courts have shown real willingness to do that where a weak claim has been
            pressed against a modest estate. The executor's reasonable defence costs generally come
            from the estate — but not if the defence itself was unreasonable.
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">What drives the number up or down</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {DRIVERS.map((d) => (
              <div key={d.t} className="rounded-sm border border-border bg-card p-5">
                <h3 className="font-serif text-lg text-foreground">{d.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.b}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">
            Before you sign a no-win-no-fee agreement
          </h2>
          <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
            <li>
              <strong className="text-foreground">The uplift.</strong> What percentage is added to
              the fees on success, and is it calculated on fees or on your recovery?
            </li>
            <li>
              <strong className="text-foreground">Disbursements.</strong> Counsel, mediator, filing
              and medical reports are often payable by you regardless of outcome. Ask in writing.
            </li>
            <li>
              <strong className="text-foreground">Adverse costs.</strong> A conditional agreement
              with your own solicitor does not protect you from an order to pay the estate's costs.
            </li>
            <li>
              <strong className="text-foreground">Offers.</strong> What happens if you decline an
              offer and later do worse? Who wears the difference?
            </li>
          </ul>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Five ways to keep the cost down</h2>
          <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-relaxed text-muted-foreground">
            <li>Give written notice within six months of death — it protects the estate from being distributed and costs almost nothing.</li>
            <li>Provide complete financial disclosure at the outset, including superannuation, debts and any interests in trusts.</li>
            <li>Be realistic about the estate's size. Proportionality is now a live factor in how costs are awarded.</li>
            <li>Make or invite a sensible offer early, in writing, so the costs risk starts running against the other side.</li>
            <li>Use one solicitor who handles both the estate administration and the dispute, rather than paying two firms to talk to each other.</li>
          </ol>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Related reading</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/estate-disputes"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Estate disputes
            </Link>
            <Link
              to="/guides/$slug"
              params={{ slug: "time-limits-contesting-a-will-queensland" }}
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Time limits for contesting a will
            </Link>
            <Link
              to="/guides/$slug"
              params={{ slug: "contesting-a-will-in-queensland" }}
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Contesting a will in Queensland
            </Link>
            <Link
              to="/guides/$slug"
              params={{ slug: "estate-contested-executor-guide-queensland" }}
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              For executors defending a claim
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein"]} />

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Costs FAQs</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Get a straight assessment first</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Tell us who died, when, what you understand the estate to include, and your relationship
            to the deceased. We will tell you whether there is a claim worth running before we talk
            about fees. Offices at {FIRM.street}, {FIRM.suburb} and {PENINSULA_ADDRESS}.
          </p>
          <div className="mt-6">
            <EnquiryForm heading="Ask about a will dispute and potential costs" defaultMatterType="wills-estates" />
          </div>
        </div>
        <div className="mt-14">
          <ExecutorIntakeForm heading="Tell us about the estate" />
        </div>
      </section>

      <CtaBand title="Left out of a will, or defending an estate?" />
      <div className="h-20" />
    </>
  );
}
